import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Spin } from 'antd';
import type { LinkedCuratorProfile, ProfilePlaylistState } from '../../utils/accountCuratorOffer';

interface OfferProfilesPlaylistsStepProps {
  linkedProfiles: LinkedCuratorProfile[];
  selectedProfileIds: string[];
  profilePlaylists: Record<string, ProfilePlaylistState>;
  errors: Record<string, string | undefined>;
  touched: Record<string, boolean>;
  disabled?: boolean;
  lockProfileSelection?: boolean;
  addProfileHint?: LinkedCuratorProfile | null;
  profileOtherOfferLabels?: Record<string, string>;
  onProfileToggle: (profileId: string, checked: boolean) => void;
  onPlaylistChange: (profileId: string, playlistIds: string[]) => void;
  onPlaylistBlur: (profileId: string) => void;
}

const OfferProfilesPlaylistsStep: React.FC<OfferProfilesPlaylistsStepProps> = ({
  linkedProfiles,
  selectedProfileIds,
  profilePlaylists,
  errors,
  touched,
  disabled = false,
  lockProfileSelection = false,
  addProfileHint,
  profileOtherOfferLabels = {},
  onProfileToggle,
  onPlaylistChange,
  onPlaylistBlur,
}) => {
  const [profileSearch, setProfileSearch] = useState('');
  const [playlistSearch, setPlaylistSearch] = useState('');
  const [focusedProfileId, setFocusedProfileId] = useState<string | null>(null);

  const filteredProfiles = useMemo(() => {
    const query = profileSearch.trim().toLowerCase();
    if (!query) return linkedProfiles;
    return linkedProfiles.filter(profile => profile.username.toLowerCase().includes(query));
  }, [linkedProfiles, profileSearch]);

  useEffect(() => {
    if (!selectedProfileIds.length) {
      setFocusedProfileId(null);
      return;
    }

    if (!focusedProfileId || !selectedProfileIds.includes(focusedProfileId)) {
      setFocusedProfileId(selectedProfileIds[0]);
    }
  }, [focusedProfileId, selectedProfileIds]);

  const focusedProfile = linkedProfiles.find(profile => profile.id === focusedProfileId) || null;
  const focusedState = focusedProfileId ? profilePlaylists[focusedProfileId] : undefined;

  const filteredPlaylists = useMemo(() => {
    const options = focusedState?.options || [];
    const query = playlistSearch.trim().toLowerCase();
    if (!query) return options;
    return options.filter(
      playlist =>
        playlist.name.toLowerCase().includes(query) ||
        playlist.playlistId.toLowerCase().includes(query)
    );
  }, [focusedState?.options, playlistSearch]);

  const totalPlaylistSelections = selectedProfileIds.reduce(
    (count, profileId) => count + (profilePlaylists[profileId]?.selectedIds.length || 0),
    0
  );

  const selectedPlaylistSet = useMemo(
    () => new Set(focusedState?.selectedIds || []),
    [focusedState?.selectedIds]
  );

  const togglePlaylist = (playlistId: string) => {
    if (!focusedProfileId || disabled) return;
    const current = profilePlaylists[focusedProfileId]?.selectedIds || [];
    const next = current.includes(playlistId)
      ? current.filter(id => id !== playlistId)
      : [...current, playlistId];
    onPlaylistChange(focusedProfileId, next);
  };

  const selectAllFilteredPlaylists = () => {
    if (!focusedProfileId || disabled) return;
    const current = profilePlaylists[focusedProfileId]?.selectedIds || [];
    const merged = Array.from(
      new Set([...current, ...filteredPlaylists.map(playlist => playlist.playlistId)])
    );
    onPlaylistChange(focusedProfileId, merged);
  };

  const clearFocusedPlaylists = () => {
    if (!focusedProfileId || disabled) return;
    onPlaylistChange(focusedProfileId, []);
  };

  return (
    <div className="offer-profiles-step">
      {addProfileHint && !selectedProfileIds.includes(addProfileHint.id) ? (
        <p className="force-message">
          Select <strong>{addProfileHint.username}</strong> on the left to include this profile.
        </p>
      ) : null}

      <div className="offer-step-layout">
        <section className="offer-step-panel offer-step-panel--profiles">
          <div className="offer-step-panel__header">
            <h3>Spotify profiles</h3>
            <span className="offer-step-panel__count">
              {selectedProfileIds.length} selected
            </span>
          </div>
          <input
            type="search"
            placeholder="Search profiles…"
            value={profileSearch}
            onChange={event => setProfileSearch(event.target.value)}
            disabled={disabled}
            className="offer-step-search"
          />
          <div className="offer-profile-list" role="list">
            {filteredProfiles.length === 0 ? (
              <div className="offer-step-empty">No profiles match your search.</div>
            ) : (
              filteredProfiles.map(profile => {
                const isSelected = selectedProfileIds.includes(profile.id);
                const isFocused = focusedProfileId === profile.id;
                const playlistCount = profilePlaylists[profile.id]?.selectedIds.length || 0;
                const isLoading = profilePlaylists[profile.id]?.loading;

                return (
                  <div
                    key={profile.id}
                    className={[
                      'offer-profile-row',
                      isSelected ? 'is-selected' : '',
                      isFocused ? 'is-focused' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="listitem"
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={disabled || lockProfileSelection}
                      onChange={event => {
                        const checked = event.target.checked;
                        onProfileToggle(profile.id, checked);
                        if (checked) {
                          setFocusedProfileId(profile.id);
                          setPlaylistSearch('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="offer-profile-row__body"
                      onClick={() => {
                        if (isSelected) {
                          setFocusedProfileId(profile.id);
                          setPlaylistSearch('');
                        }
                      }}
                      disabled={!isSelected || disabled}
                    >
                      <span className="offer-profile-row__name">{profile.username}</span>
                      {!isSelected && profileOtherOfferLabels[profile.id] ? (
                        <span className="offer-profile-row__meta offer-profile-row__other-offer">
                          On: {profileOtherOfferLabels[profile.id]}
                        </span>
                      ) : null}
                      {isSelected ? (
                        <span className="offer-profile-row__meta">
                          {isLoading ? (
                            <>
                              <Spin size="small" /> Loading…
                            </>
                          ) : (
                            `${playlistCount} playlist${playlistCount === 1 ? '' : 's'}`
                          )}
                        </span>
                      ) : null}
                    </button>
                  </div>
                );
              })
            )}
          </div>
          {touched.profiles && errors.profiles ? (
            <div className="error-message">{errors.profiles}</div>
          ) : null}
        </section>

        <section className="offer-step-panel offer-step-panel--playlists">
          <div className="offer-step-panel__header">
            <h3>
              {focusedProfile
                ? `Playlists for ${focusedProfile.username}`
                : 'Select a profile'}
            </h3>
            {focusedProfile ? (
              <span className="offer-step-panel__count">
                {(focusedState?.selectedIds || []).length} selected
              </span>
            ) : null}
          </div>

          {!focusedProfile ? (
            <div className="offer-step-empty offer-step-empty--panel">
              Choose one or more profiles on the left, then pick playlists here.
            </div>
          ) : (
            <>
              <input
                type="search"
                placeholder="Search playlists…"
                value={playlistSearch}
                onChange={event => setPlaylistSearch(event.target.value)}
                disabled={disabled || Boolean(focusedState?.loading)}
                className="offer-step-search"
              />

              <div className="offer-playlist-toolbar">
                <button
                  type="button"
                  className="offer-playlist-toolbar__action"
                  onClick={selectAllFilteredPlaylists}
                  disabled={disabled || Boolean(focusedState?.loading) || !filteredPlaylists.length}
                >
                  Select {playlistSearch.trim() ? 'filtered' : 'all'}
                </button>
                <button
                  type="button"
                  className="offer-playlist-toolbar__action"
                  onClick={clearFocusedPlaylists}
                  disabled={
                    disabled ||
                    Boolean(focusedState?.loading) ||
                    !(focusedState?.selectedIds || []).length
                  }
                >
                  Clear
                </button>
              </div>

              {focusedState?.loading ? (
                <div className="offer-step-loading">
                  <Spin /> Loading playlists…
                </div>
              ) : focusedState?.error ? (
                <div className="error-message">{focusedState.error}</div>
              ) : filteredPlaylists.length === 0 ? (
                <div className="offer-step-empty offer-step-empty--panel">
                  {focusedState?.options.length
                    ? 'No playlists match your search.'
                    : 'This profile needs at least one playlist you fully control.'}
                </div>
              ) : (
                <div
                  className="offer-playlist-list"
                  onBlur={() => onPlaylistBlur(focusedProfile.id)}
                >
                  {filteredPlaylists.map(playlist => {
                    const checked = selectedPlaylistSet.has(playlist.playlistId);
                    return (
                      <label
                        key={playlist.playlistId}
                        className={`offer-playlist-row ${checked ? 'is-selected' : ''}`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onChange={() => togglePlaylist(playlist.playlistId)}
                        />
                        <span className="offer-playlist-row__name" title={playlist.name}>
                          {playlist.name}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {touched[focusedProfile.id] && errors[focusedProfile.id] ? (
                <div className="error-message">{errors[focusedProfile.id]}</div>
              ) : null}
            </>
          )}
        </section>
      </div>

      <div className="offer-selection-summary">
        {selectedProfileIds.length} profile{selectedProfileIds.length === 1 ? '' : 's'} ·{' '}
        {totalPlaylistSelections} playlist{totalPlaylistSelections === 1 ? '' : 's'} selected
      </div>
    </div>
  );
};

export default OfferProfilesPlaylistsStep;
